import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { openSignedDoc } from '../lib/uploads.js';

// Reusable document linker. Mounts inside an equipment or task detail
// view; lists docs currently linked, lets a manager add/remove links from
// the existing knowledge_documents table. Staff (canEdit=false) just see
// the list with download buttons for any attached PDF.
//
// scope: 'equipment' | 'tasks' — used to build the API path
// id: equipment_id or task_id
// userId: passed to /uploads?u= so the staff click-to-download works
//        without bouncing through fetch+blob
export function DocumentLinker({ scope, id, canEdit, userId }) {
  const headers = { 'x-user-id': String(userId) };
  const [linked, setLinked] = useState([]);
  const [picker, setPicker] = useState([]);
  const [show, setShow] = useState(false); // editor open?
  const [draft, setDraft] = useState(new Set());
  const [savingMsg, setSavingMsg] = useState('');

  async function loadLinked() {
    try {
      const rows = await api('GET', `/api/${scope}/${id}/documents`, null, headers);
      setLinked(Array.isArray(rows) ? rows : []);
    } catch (_) { setLinked([]); }
  }
  useEffect(() => { loadLinked(); }, [scope, id]);

  async function openEditor() {
    setSavingMsg('');
    try {
      const all = await api('GET', '/api/knowledge-docs/picker', null, headers);
      setPicker(Array.isArray(all) ? all : []);
      setDraft(new Set(linked.map(d => d.id)));
      setShow(true);
    } catch (e) { setSavingMsg(`Couldn't load: ${e.message || e}`); }
  }

  function toggleDraft(docId) {
    setDraft(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId); else next.add(docId);
      return next;
    });
  }

  async function save() {
    setSavingMsg('Saving…');
    try {
      const ids = Array.from(draft);
      await api('PUT', `/api/${scope}/${id}/documents`, { document_ids: ids }, headers);
      setSavingMsg('Saved.');
      setShow(false);
      loadLinked();
    } catch (e) { setSavingMsg(`Save failed: ${e.message || e}`); }
  }

  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>Linked documents</div>
          <div style={{ fontSize: 11, color: '#9e9e9e' }}>
            Cleaning SOPs, manuals, and other PDFs from the Knowledge Hub.
          </div>
        </div>
        {canEdit && (
          <button onClick={openEditor} className="btn-secondary" style={{ fontSize: 12 }}>
            {linked.length === 0 ? '+ Link document' : 'Manage links'}
          </button>
        )}
      </div>

      {linked.length === 0 ? (
        <div style={{ fontSize: 12, color: '#9e9e9e' }}>No documents linked yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {linked.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                       border: '1px solid #e5e7eb', borderRadius: 8, background: '#fafafa' }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>📄</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>{d.title}</div>
                <div style={{ fontSize: 11, color: '#9e9e9e' }}>
                  {d.type ? d.type.replace(/_/g, ' ') : '—'}{d.category ? ` · ${d.category}` : ''}
                  {d.attachment_filename ? ` · ${d.attachment_filename}` : ''}
                </div>
              </div>
              {d.attachment_filename && (
                <a href="#"
                   onClick={e => { e.preventDefault();
                     openSignedDoc(d.id, d.attachment_filename, { 'x-user-id': String(userId) }); }}
                   style={{ flexShrink: 0, fontSize: 12, color: 'var(--forest)', fontWeight: 600,
                             padding: '4px 10px', border: '1px solid #d0d4d9', borderRadius: 6,
                             background: '#fff', textDecoration: 'none' }}>
                  Open
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {show && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                       display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
             onClick={() => setShow(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 18, maxWidth: 560, width: '100%',
                         maxHeight: '80vh', overflow: 'auto' }}
               onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 700 }}>Link documents</div>
              <button onClick={() => setShow(false)} style={{ background: 'transparent', border: 'none', fontSize: 24, color: '#9e9e9e', cursor: 'pointer' }}>×</button>
            </div>
            {picker.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9e9e9e' }}>No documents in the Knowledge Hub yet. Add one in Knowledge Hub → Policies & SOPs first.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: '50vh', overflowY: 'auto' }}>
                {picker.map(d => (
                  <label key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                                                cursor: 'pointer', border: '1px solid #f3f4f6', borderRadius: 6 }}>
                    <input type="checkbox" checked={draft.has(d.id)} onChange={() => toggleDraft(d.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>{d.title}</div>
                      <div style={{ fontSize: 11, color: '#9e9e9e' }}>
                        {d.type ? d.type.replace(/_/g, ' ') : '—'}{d.category ? ` · ${d.category}` : ''}
                        {d.attachment_filename ? ` · 📎 ${d.attachment_filename}` : ''}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
              <button onClick={() => setShow(false)} className="btn-secondary" style={{ fontSize: 12 }}>Cancel</button>
              <button onClick={save}
                style={{ background: 'var(--forest)', color: '#fff', border: 'none',
                          borderRadius: 6, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Save links ({draft.size})
              </button>
            </div>
            {savingMsg && <div style={{ fontSize: 11, color: savingMsg.includes('failed') ? '#dc2626' : '#16a34a', marginTop: 6 }}>{savingMsg}</div>}
          </div>
        </div>
      )}
    </div>
  );
}
